/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from '@/components/link';
import type { ShopperSearch } from '@/scapi';
import { createProductUrl, getImagesForColor } from '@/lib/product/product-utils';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { cn } from '@/lib/utils';
import { ProductImage } from './product-image';
import ImageNavArrows from '@/components/image-nav-arrows';
import { useTranslation } from 'react-i18next';

interface ProductImageContainerProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    selectedColorValue?: string | null;
    className?: string;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /** Image aspect ratio (width/height). If provided, calculates height based on viewport width. Defaults to 1 (square) */
    imgAspectRatio?: number;
    /** Show prev/next navigation arrows when multiple images are available */
    showNavigationArrows?: boolean;
}

const ProductImageContainer = ({
    product,
    selectedColorValue = null,
    className,
    handleProductClick,
    imgAspectRatio = 1,
    showNavigationArrows = false,
}: ProductImageContainerProps) => {
    const { t } = useTranslation('product');
    const [, setSelectedImageIndex] = useState(0);

    // Primary images (hi-res for display quality)
    const hiResImages = useMemo(
        () => getImagesForColor(product, selectedColorValue, 'hi-res'),
        [product, selectedColorValue]
    );
    // Fallback to 'large' viewType for hover — catalogs often have multiple large angles
    const largeImages = useMemo(
        () => getImagesForColor(product, selectedColorValue, 'large'),
        [product, selectedColorValue]
    );

    const primaryImage = hiResImages[0] ?? largeImages[0];
    const primarySrc = primaryImage?.disBaseLink || primaryImage?.link || '';

    // Pick hover image: first 'large' image with a different src than primary, else second hi-res
    const hoverImage = useMemo(() => {
        const altLarge = largeImages.find(
            (img) => (img.disBaseLink || img.link) !== primarySrc && (img.disBaseLink || img.link)
        );
        if (altLarge) return altLarge;
        return hiResImages[1] ?? null;
    }, [largeImages, hiResImages, primarySrc]);

    // On hover (non-arrow mode), show second image if available
    const canHoverSwap = !showNavigationArrows && !!hoverImage;
    const imageAltFallback = product.productName || t('imageAlt') || 'Product Image';

    // Report the primary image URL to the dynamic image context, if available
    const imageContext = useDynamicImageContext();
    primarySrc && imageContext?.addSource(primarySrc);

    const handleClick = useCallback(() => {
        handleProductClick?.(product);
    }, [handleProductClick, product]);

    // When a non-square aspect ratio is requested, apply it via the native CSS
    // `aspect-ratio` property. The legacy padding-bottom percentage trick is not
    // used here because it conflicts with the native property and collapses the
    // image height to zero.
    const heightStyle = imgAspectRatio !== 1 ? { aspectRatio: `${imgAspectRatio}` } : {};

    return (
        <div
            className={cn(
                'relative overflow-hidden bg-[#F4F4F4] flex flex-col',
                showNavigationArrows && 'group/image',
                imgAspectRatio === 1 && 'aspect-square',
                className
            )}
            style={heightStyle}>
            {/* RGBARRY-CUSTOMIZATION: keep the image link semantic and keyboard-accessible. A clickable
                anchor hidden with aria-hidden could still receive pointer focus, which made Chrome reject
                the hidden state and emit an accessibility warning. */}
            <Link
                to={createProductUrl(product.productId, selectedColorValue, 'color', null, product.productName)}
                onClick={handleClick}
                className="block w-full h-full flex-1"
                aria-label={`View ${product.productName || imageAltFallback}`}>
                <ProductImage
                    src={primarySrc}
                    alt={primaryImage?.alt || imageAltFallback}
                    className={cn(
                        'w-full h-full object-contain mix-blend-multiply transition-opacity duration-300',
                        canHoverSwap && 'group-hover/imgswap:opacity-0'
                    )}
                    widths={imageContext?.widths}
                />
                {/* Secondary image — cross-fades in on hover */}
                {canHoverSwap && hoverImage && (
                    <ProductImage
                        src={hoverImage.disBaseLink || hoverImage.link || ''}
                        alt={hoverImage.alt || imageAltFallback}
                        className="absolute inset-0 w-full h-full object-contain mix-blend-multiply transition-opacity duration-300 opacity-0 group-hover/imgswap:opacity-100"
                        widths={imageContext?.widths}
                        // RGBARRY-CUSTOMIZATION: the hover image only reveals on pointer hover, which never
                        // happens on touch — so load it lazily at low priority instead of eagerly. Without this
                        // it doubled the grid's initial image requests (every tile fetched _01 AND _02 up front)
                        // and, on critical tiles, inherited priority=high from the grid's DynamicImageProvider,
                        // contending with the real LCP image on the mobile connection. Explicit priority="low"
                        // overrides that context; desktop still gets the swap image on hover.
                        loading="lazy"
                        priority="low"
                    />
                )}
            </Link>
            {/* Navigation Arrows - visible on hover */}
            {showNavigationArrows && hiResImages.length > 1 && (
                <ImageNavArrows
                    imageCount={hiResImages.length}
                    onIndexChange={setSelectedImageIndex}
                    className="opacity-0 group-hover/image:opacity-100 transition-opacity"
                />
            )}
        </div>
    );
};

export { ProductImageContainer };
